import { CalendarDays, Download, Home } from 'lucide-react'

import { PERMISSIONS } from '@/constants/permissions'
import { createLink, createPopover, getUrl } from '@/index'

export const HOME = createLink({
  link: getUrl('/'),
  icon: Home,
  label: 'Home',
})

export const ADMIN = {
  STRATEGY: createLink({
    label: 'Strategy',
    link: getUrl('/admin/strategies'),
    permissions: [PERMISSIONS.WRITE_ADMIN_FUNDS],
  }),
  FUNDS: createLink({
    label: 'Funds',
    link: getUrl('/admin/funds'),
    permissions: [PERMISSIONS.READ_ADMIN_FUNDS],
  }),
}

export const FUND_CALENDAR = createLink({
  label: 'Calendar',
  icon: CalendarDays,
  link: getUrl('/admin/calendar'),
  permissions: [PERMISSIONS.READ_ADMIN_FUNDS, PERMISSIONS.WRITE_ADMIN_FUNDS],
})

export const ADMIN_LINKS = [ADMIN.STRATEGY, ADMIN.FUNDS, FUND_CALENDAR]

export const FEEDS = {
  PROVIDERS: createLink({
    label: 'Providers',
    link: getUrl('/feeds/providers'),
    permissions: [PERMISSIONS.READ_FEEDS],
  }),
  IC_GUIDELINES: createLink({
    label: 'IC Guidelines',
    link: getUrl('/feeds/ic-guidelines'),
    permissions: [PERMISSIONS.READ_IC_GUIDELINES_STATUS],
  }),
}

export const FEEDS_LINKS = [FEEDS.PROVIDERS, FEEDS.IC_GUIDELINES]

export const MONITORING = {
  LOGS: createLink({
    label: 'Logs',
    link: getUrl('/monitoring/logs'),
    permissions: [PERMISSIONS.READ_LOGS],
  }),
  MONITORING: createLink({
    label: 'Task Data',
    link: getUrl('/monitoring'),
    permissions: [PERMISSIONS.READ_SCHEDULED_TASKS],
  }),
  CUSTOM_JOBS: createLink({
    label: 'Custom Jobs',
    link: getUrl('/monitoring/custom-jobs'),
    permissions: [PERMISSIONS.RUN_LOGS],
  }),
}

export const MONITORING_LINKS = [MONITORING.LOGS, MONITORING.MONITORING, MONITORING.CUSTOM_JOBS]

export const OPERATIONS = {
  RISK: createLink({
    label: 'Risk & Calibration',
    link: getUrl('/operations/risk-calibration'),
    permissions: [PERMISSIONS.READ_RISK_CALIBRATION],
  }),
  TX_REPORTING: createLink({
    label: 'TX Reporting',
    link: getUrl('/operations/transaction-reporting'),
    permissions: [PERMISSIONS.READ_TX_REPORTING],
  }),
  NAV_UPDATE: createLink({
    label: 'Nav Update',
    link: getUrl('/operations/nav-update'),
    permissions: [PERMISSIONS.READ_NAV_UPDATE],
  }),
  AUTOMATED_PRICING: createLink({
    label: 'Price Validation',
    link: getUrl('/operations/automated-pricing'),
    permissions: [PERMISSIONS.READ_PRICE_VALIDATION],
  }),
  MAPPINGS: createLink({
    label: 'Mappings',
    link: getUrl('/operations/mappings'),
    permissions: [PERMISSIONS.READ_MAPPINGS],
  }),
  MONTHLY_ROLL: createLink({
    label: 'Monthly Fx Roll',
    link: getUrl('/operations/monthly-fx-roll'),
    permissions: [PERMISSIONS.READ_MONTHLY_ROLL],
  }),
  NT_HEDGING_FWD: createLink({
    label: 'NT Hedging Fwd',
    link: getUrl('/operations/nt-hedging-fwd'),
    permissions: [PERMISSIONS.READ_NT_HEDGING_FXFWD],
  }),
  DB_REC: {
    AUTOMATED_NAV: createLink({
      label: 'Automated Nav',
      link: getUrl('/operations/db-rec/automated-nav'),
      permissions: [PERMISSIONS.READ_AUTOMATED_NAV],
    }),
    POSITIONS_PNL: createLink({
      label: 'Positions & P&L',
      link: getUrl('/operations/db-rec/positions-pnl'),
      permissions: [PERMISSIONS.READ_POSITIONS_PNL],
    }),
  },
  PNL_VALIDATION: createLink({
    label: 'P&L Validation',
    link: getUrl('/operations/pnl-validation'),
    permissions: [PERMISSIONS.READ_PNL_VALIDATION],
  }),
}

export const OPERATIONS_LINKS = [
  OPERATIONS.RISK,
  OPERATIONS.TX_REPORTING,
  OPERATIONS.NAV_UPDATE,
  OPERATIONS.AUTOMATED_PRICING,
  OPERATIONS.MAPPINGS,
  OPERATIONS.MONTHLY_ROLL,
  OPERATIONS.NT_HEDGING_FWD,
  OPERATIONS.PNL_VALIDATION,
  createPopover({
    label: 'DB Rec',
    children: [OPERATIONS.DB_REC.AUTOMATED_NAV, OPERATIONS.DB_REC.POSITIONS_PNL],
  }),
]

export const TRADING = {
  TRADES: createLink({
    label: 'Trades',
    link: getUrl('/trading'),
    permissions: [PERMISSIONS.READ_TRADES],
  }),
  BOOK: createLink({
    label: 'Book Trade',
    link: getUrl('/trading/new-trade'),
    permissions: [PERMISSIONS.WRITE_TRADES],
  }),
  BONDS_DATA_REC: createLink({
    label: 'Bonds Data Rec',
    link: getUrl('/trading/bonds-data-rec'),
    permissions: [PERMISSIONS.READ_TRADES],
  }),
  DATA_UPLOAD: createLink({
    label: 'Data Upload',
    link: getUrl('/trading/data-upload'),
    permissions: [PERMISSIONS.WRITE_TRADING_DATA],
  }),
}

export const TRADING_LINKS = [TRADING.TRADES, TRADING.BONDS_DATA_REC, TRADING.DATA_UPLOAD, TRADING.BOOK]

export const FUNDS = {
  IC_GUIDELINES: createLink({
    label: 'IC Guidelines',
    link: getUrl('/funds/ic-guidelines'),
    permissions: [PERMISSIONS.READ_IC_GUIDELINES],
  }),
  IC_GUIDELINES_V2: createLink({
    label: 'IC Guidelines *V2*',
    link: getUrl('/funds/ic-guidelines-v2'),
    permissions: [PERMISSIONS.READ_IC_GUIDELINES],
  }),
  IC_REPORT: createLink({
    label: 'IC Report',
    link: getUrl('/funds/ic-report'),
    permissions: [PERMISSIONS.READ_IC_REPORT],
  }),
  MANAGEMENT_REPORT: createLink({
    label: 'Management Report',
    link: getUrl('/funds/management-report'),
    permissions: [PERMISSIONS.READ_IC_REPORT],
  }),
  FLOWS: createLink({
    label: 'Flows',
    link: getUrl('/funds/flows'),
    permissions: [PERMISSIONS.READ_FLOWS],
  }),
}

export const FUNDS_LINKS = [
  FUNDS.FLOWS,
  FUNDS.IC_GUIDELINES,
  FUNDS.IC_GUIDELINES_V2,
  FUNDS.IC_REPORT,
  FUNDS.MANAGEMENT_REPORT,
]

export const ACCOUNT = {
  DASHBOARD: createLink({
    label: 'My Account',
    link: getUrl('/account'),
  }),
}

export const ACCOUNT_LINKS = [ACCOUNT.DASHBOARD]

export const FLOW = {
  POSMON: createLink({
    label: 'Posmon',
    link: getUrl('/flow/posmon'),
    permissions: [PERMISSIONS.READ_FLOW_POSMON],
  }),
  WEBSITE: createLink({
    label: 'Website',
    link: getUrl('/flow/website'),
    permissions: [PERMISSIONS.READ_WEBSITE],
  }),
  RFQ: createLink({
    label: 'RFQ Analytics',
    link: getUrl('/flow/rfq'),
    permissions: [PERMISSIONS.READ_RFQ_DATA],
  }),
  PRICING_MATRIX: createLink({
    label: 'Pricing Matrix',
    link: getUrl('/flow/pricing-matrix'),
    permissions: [PERMISSIONS.READ_RFQ_DATA],
  }),
  PRICING_INTERFACE: createLink({
    label: 'Pricing Interface',
    link: getUrl('/flow/pricing-interface'),
    permissions: [PERMISSIONS.READ_PRICING_INTERFACE],
  }),
  CLIENT_PRICING_REPORT: createLink({
    label: 'Client Pricing Report',
    link: getUrl('/flow/client-pricing-report'),
    permissions: [PERMISSIONS.READ_CLIENT_PRICING],
  }),
}

export const FLOW_LINKS = [
  FLOW.POSMON,
  FLOW.WEBSITE,
  FLOW.PRICING_INTERFACE,
  FLOW.CLIENT_PRICING_REPORT,
  createPopover({
    label: 'Analytics',
    children: [FLOW.RFQ, FLOW.PRICING_MATRIX],
  }),
]

export const INSTRUMENTS = {
  ALL: createLink({
    label: 'Instruments',
    link: getUrl('/instruments'),
    permissions: [PERMISSIONS.READ_INSTRUMENTS],
  }),
  NEW: createLink({
    label: 'New Instrument',
    link: getUrl('/instruments/new'),
    permissions: [PERMISSIONS.CREATE_INSTRUMENTS],
  }),
  TIME_SERIES: createLink({
    label: 'Time Series',
    link: getUrl('/instruments/time-series'),
    icon: Download,
    permissions: [PERMISSIONS.READ_TIME_SERIES],
  }),
  MARKETDATA: createLink({
    label: 'Market Data',
    link: getUrl('/instruments/marketdata'),
    permissions: [PERMISSIONS.READ_MARKETDATA],
  }),
  MARKET_SUMMARY: createLink({
    label: 'Market Summary',
    link: getUrl('/instruments/market-summary'),
    permissions: [PERMISSIONS.READ_MARKET_SUMMARY],
  }),
}

export const INSTRUMENTS_LINKS = [
  INSTRUMENTS.ALL,
  INSTRUMENTS.NEW,
  INSTRUMENTS.TIME_SERIES,
  INSTRUMENTS.MARKETDATA,
  INSTRUMENTS.MARKET_SUMMARY,
]

export const SALES = {
  PERF_REPORT: createLink({
    label: 'Perf Report',
    link: getUrl('/sales/perf-report'),
    permissions: [PERMISSIONS.READ_PERF_REPORT],
  }),
}

export const SALES_LINKS = [SALES.PERF_REPORT]

export const SMARTRADE = {
  TRADES: createLink({
    label: 'Trades',
    link: getUrl('/smartrade/trades'),
    permissions: [PERMISSIONS.READ_SMARTRADE_TRADES],
  }),
  DATA: createLink({
    label: 'Data',
    link: getUrl('/smartrade/data'),
    permissions: [PERMISSIONS.READ_SMARTRADE_DATA],
  }),
  CLIENTS: createLink({
    label: 'Clients',
    link: getUrl('/smartrade/clients'),
    permissions: [PERMISSIONS.READ_SMARTRADE_DATA],
  }),
}

export const SMARTRADE_LINKS = [SMARTRADE.TRADES, SMARTRADE.DATA, SMARTRADE.CLIENTS]

export const PM = {
  ATTRIBUTIONS: {
    DATA_PRESENCE: createLink({
      label: 'Data Presence',
      link: getUrl('/pm/attributions/data-presence'),
      permissions: [PERMISSIONS.READ_DATA_PRESENCE],
    }),
    RISK_PREMIA: createLink({
      label: 'Risk Premia',
      link: getUrl('/pm/attributions/risk-premia'),
      permissions: [PERMISSIONS.READ_RISK_PREMIA],
    }),
    FIXED_INCOME: createLink({
      label: 'Fixed Income',
      link: getUrl('/pm/attributions/fixed-income'),
      permissions: [PERMISSIONS.READ_FIXED_INCOME],
    }),
    FUND_ATTRIBUTIONS: createLink({
      label: 'Fund Attributions',
      link: getUrl('/pm/attributions/fund-attributions'),
      permissions: [PERMISSIONS.READ_FUND_ATTRIBUTIONS],
    }),
  },
  PREMIALAB: {
    DAILY_COMPOSITION: createLink({
      label: 'Daily Composition',
      link: getUrl('/pm/premialab/daily-composition'),
      permissions: [PERMISSIONS.READ_DAILY_COMPOSITION],
    }),
  },
  UPLOAD_TRACK: createLink({
    label: 'Upload Track',
    link: getUrl('/pm/upload-track'),
    permissions: [PERMISSIONS.READ_UPLOAD_TRACK],
  }),
}

export const PM_LINKS = [
  createPopover({
    label: 'Attributions',
    children: [
      PM.ATTRIBUTIONS.DATA_PRESENCE,
      PM.ATTRIBUTIONS.RISK_PREMIA,
      PM.ATTRIBUTIONS.FIXED_INCOME,
      PM.ATTRIBUTIONS.FUND_ATTRIBUTIONS,
    ],
  }),
  createPopover({
    label: 'Premialab',
    children: [PM.PREMIALAB.DAILY_COMPOSITION],
  }),
  PM.UPLOAD_TRACK,
]
